import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import Calendar from '../../components/Calendar';
import Modal from '../../components/Modal';
import './Calendar.css';

const EVENT_TYPE_OPTIONS = [
  { value: 'college', label: 'College' },
  { value: 'exam', label: 'Tentamen' },
  { value: 'resit', label: 'Hertentamen' },
  { value: 'bp-submission', label: 'Inleveren Beroepsproduct' },
  { value: 'so-submission', label: 'Inleveren Schriftelijke opdracht' },
  { value: 'presentation', label: 'Presentatie' },
  { value: 'adviesrapport-submission', label: 'Inleveren Adviesrapport' },
  { value: 'event', label: 'Evenement' }
];

const CALENDAR_TIME_ZONE = 'America/Paramaribo';
const PARAMARIBO_OFFSET = '-03:00';

const toParamariboDateTime = (date, time) => {
  const rawTime = String(time || '00:00').trim();
  const normalizedTime = rawTime.length === 5 ? `${rawTime}:00` : rawTime;
  return `${date}T${normalizedTime}${PARAMARIBO_OFFSET}`;
};

const toInputDateInParamaribo = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CALENDAR_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);

  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  if (!year || !month || !day) return '';
  return `${year}-${month}-${day}`;
};

const toInputTimeInParamaribo = (value) => {
  if (!value) return '09:00';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '09:00';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CALENDAR_TIME_ZONE,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit'
  }).formatToParts(date);

  const hour = parts.find((part) => part.type === 'hour')?.value;
  const minute = parts.find((part) => part.type === 'minute')?.value;
  if (!hour || !minute) return '09:00';
  return `${hour}:${minute}`;
};

const isValidDateRange = (date, startTime, endTime) => {
  const start = new Date(toParamariboDateTime(date, startTime));
  const end = new Date(toParamariboDateTime(date, endTime));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  return end > start;
};

const EVENT_TYPE_LABELS = EVENT_TYPE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {
  lesson: 'College',
  'bp-so-submission': 'Inleveren BP/SO',
  'report-submission': 'Verslag indiening',
  'report-review': 'Verslag bespreking',
  activity: 'Activiteit'
});

const EVENT_EXAMTYPE_OPTIONS = {
  exam: [{ value: 'kennistentamen', label: 'Kennistentamen' }],
  resit: [{ value: 'kennistentamen', label: 'Kennistentamen' }],
  'bp-submission': [{ value: 'beroepsproduct', label: 'Beroepsproduct' }],
  'so-submission': [{ value: 'schriftelijke-opdracht', label: 'Schriftelijke opdracht' }],
  presentation: [{ value: 'presentatievorm', label: 'Presentatievorm' }],
  'adviesrapport-submission': [{ value: 'adviesrapport', label: 'Adviesrapport' }]
};

const REQUIRED_EXAM_EVENT_TYPES = new Set(Object.keys(EVENT_EXAMTYPE_OPTIONS));

const EXAM_POINT_TO_EXAM_TYPE = {
  'kennistentamen': 'kennistentamen',
  'onderzoeksverslag/ voorstel': 'onderzoeksverslag-voorstel',
  'onderzoeksverslag/voorstel': 'onderzoeksverslag-voorstel',
  'beroepsproduct': 'beroepsproduct',
  'adviesrapport': 'adviesrapport',
  'reflectieverslag/ portfolio': 'reflectieverslag-portfolio',
  'reflectieverslag/portfolio': 'reflectieverslag-portfolio',
  'schriftelijke opdracht': 'schriftelijke-opdracht',
  'presentatievorm': 'presentatievorm'
};

const normalizeExamPointName = (name) => String(name || '').trim().toLowerCase();

const getCourseExamTypeSet = (course) => {
  return new Set(
    (course?.examPoints || [])
      .map((ep) => EXAM_POINT_TO_EXAM_TYPE[normalizeExamPointName(ep?.name)])
      .filter(Boolean)
  );
};

const getCourseByIdFromList = (courses, courseId) => {
  return (courses || []).find((course) => String(course.id || course._id) === String(courseId));
};

const getExamTypeOptions = (eventType, courseId, courses) => {
  const baseOptions = EVENT_EXAMTYPE_OPTIONS[eventType] || [];
  if (!baseOptions.length) return [];
  const selectedCourse = getCourseByIdFromList(courses, courseId);
  if (!selectedCourse) return [];
  const courseExamTypes = getCourseExamTypeSet(selectedCourse);
  return baseOptions.filter((option) => courseExamTypes.has(option.value));
};

const normalizeEventTypeForForm = (eventType, examType) => {
  const normalizedType = String(eventType || 'college');
  if (normalizedType === 'bp-so-submission') {
    return String(examType || '').toLowerCase() === 'schriftelijke-opdracht' ? 'so-submission' : 'bp-submission';
  }
  if (normalizedType === 'report-submission') {
    return 'adviesrapport-submission';
  }
  if (normalizedType === 'report-review' || normalizedType === 'activity') {
    return 'event';
  }
  return normalizedType;
};

const buildAutoTitle = (courseId, eventType, courses) => {
  const selectedCourse = getCourseByIdFromList(courses, courseId);
  if (!selectedCourse) return '';
  const typeLabel = EVENT_TYPE_LABELS[eventType] || eventType;
  return `${selectedCourse.code} - ${selectedCourse.name} - ${typeLabel}`;
};

const AdminCalendar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedLocation } = useOutletContext() || {};
  const { data, getCourseById, addSchedule, updateSchedule, addCalendarEvent, updateCalendarEvent, deleteCalendarEvent } = useData();

  const getId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return String(value.id || value._id || '');
    return String(value);
  };
  
  // Beschikbare studies en cohorts ophalen uit de data context
  const studies = data.studies || [];
  const cohorts = data.cohorts || [];

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [newSchedule, setNewSchedule] = useState({
    eventType: 'college',
    examType: '',
    title: '',
    description: '',
    studyId: '',
    studyYearId: '',
    cohortId: '',
    courseId: '',
    mode: 'onsite',
    meetingUrl: '',
    date: '',
    startTime: '09:00',
    endTime: '17:00',
    location: user?.location || 'Paramaribo'
  });

  // Build events van schedules
  const getEvents = () => {
    return (data.schedules || []).map((schedule) => {
      const course = getCourseById(schedule.courseId);
      const start = schedule.start || schedule.startDateTime || (schedule.date && schedule.startTime ? `${schedule.date}T${schedule.startTime}` : null);
      const end = schedule.end || schedule.endDateTime || (schedule.date && schedule.endTime ? `${schedule.date}T${schedule.endTime}` : null);
      const normalizedEventType = normalizeEventTypeForForm(schedule.eventType || schedule.type || 'college', schedule.examType || null);
      const baseTitle = schedule.title || course?.name || '';
      const fullTitle = (course?.code && !String(baseTitle).startsWith(`${course.code} - `))
        ? `${course.code} - ${baseTitle}`
        : baseTitle;

      return {
        id: schedule.id || schedule._id,
        title: fullTitle,
        start,
        end,
        location: schedule.location,
        courseId: schedule.courseId,
        type: normalizedEventType,
        eventType: normalizedEventType,
        examType: schedule.examType || null,
        mode: schedule.mode || 'onsite',
        meetingUrl: schedule.meetingUrl || '',
        description: schedule.description || '',
        // Studie en cohort id's doorgeven aan de kalender props
        studyId: schedule.studyId || course?.studyId || '',
        cohortId: schedule.cohortId || course?.cohortId || '',
        extendedProps: {
          course,
          schedule
        }
      };
    }).filter((event) => event.start && event.end);
  };

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
  };

  const openEditEvent = () => {
    if (!selectedEvent) return;
    const event = selectedEvent;
    const scheduleRaw = event.extendedProps?.schedule || {};
    const startValue = scheduleRaw.startDateTime || event?.start || null;
    const endValue = scheduleRaw.endDateTime || event?.end || null;

    const rawEventType = scheduleRaw.eventType || event.extendedProps?.eventType || event.extendedProps?.type || 'college';
    const currentExamType = scheduleRaw.examType || event.extendedProps?.examType || '';
    const normalizedEventType = normalizeEventTypeForForm(rawEventType, currentExamType);

    setEditEvent({
      id: event.id,
      title: event.title || '',
      description: scheduleRaw.description || event.extendedProps?.description || '',
      eventType: normalizedEventType,
      examType: currentExamType,
      courseId: getId(scheduleRaw.courseId || event.extendedProps?.courseId),
      studyId: getId(scheduleRaw.studyId || event.studyId),
      cohortId: getId(scheduleRaw.cohortId || event.cohortId),
      studyYearId: scheduleRaw.studyYearId || scheduleRaw.course?.studyYear?.id || scheduleRaw.course?.studyYear?._id || '',
      mode: scheduleRaw.mode || event.extendedProps?.mode || 'onsite',
      meetingUrl: scheduleRaw.meetingUrl || event.extendedProps?.meetingUrl || '',
      location: scheduleRaw.location || event.extendedProps?.location || user?.location || 'Paramaribo',
      date: toInputDateInParamaribo(startValue),
      startTime: toInputTimeInParamaribo(startValue),
      endTime: toInputTimeInParamaribo(endValue)
    });
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editEvent?.id || !editEvent?.date) return;
    if (!isValidDateRange(editEvent.date, editEvent.startTime, editEvent.endTime)) {
      window.alert('Eindtijd moet na starttijd liggen.');
      return;
    }

    const requiresExamType = REQUIRED_EXAM_EVENT_TYPES.has(editEvent.eventType);
    const availableExamOptions = getExamTypeOptions(editEvent.eventType, editEvent.courseId, allCourses);
    if (requiresExamType && !editEvent.courseId) {
      window.alert('Kies eerst een vak voor dit evenementtype.');
      return;
    }
    if (requiresExamType && availableExamOptions.length === 0) {
      window.alert('Dit vak heeft geen passend examenpunt voor dit evenementtype.');
      return;
    }

    const payload = {
      title: editEvent.title,
      description: editEvent.description,
      eventType: editEvent.eventType,
      examType: editEvent.examType || undefined,
      courseId: getId(editEvent.courseId) || undefined,
      studyId: getId(editEvent.studyId) || undefined,
      studyYearId: getId(editEvent.studyYearId) || undefined,
      cohortId: getId(editEvent.cohortId) || undefined,
      mode: editEvent.mode,
      meetingUrl: editEvent.mode === 'online' ? editEvent.meetingUrl : undefined,
      location: editEvent.location,
      startDateTime: toParamariboDateTime(editEvent.date, editEvent.startTime),
      endDateTime: toParamariboDateTime(editEvent.date, editEvent.endTime)
    };

    try {
      if (editEvent.eventType === 'college' && editEvent.courseId) {
        await updateSchedule(editEvent.courseId, editEvent.id, payload);
      } else {
        await updateCalendarEvent(editEvent.id, payload);
      }
      setEditEvent(null);
      setSelectedEvent(null);
    } catch (error) {
      window.alert(error.message || 'Bewerken van kalender item mislukt');
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;
    if (!window.confirm('Weet u zeker dat u dit kalender item wilt verwijderen?')) return;

    try {
      await deleteCalendarEvent(selectedEvent.id);
      setSelectedEvent(null);
      setEditEvent(null);
    } catch (error) {
      window.alert(error.message || 'Verwijderen van kalender item mislukt');
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    if (!newSchedule.date) return;
    if (!isValidDateRange(newSchedule.date, newSchedule.startTime, newSchedule.endTime)) {
      window.alert('Eindtijd moet na starttijd liggen.');
      return;
    }

    const requiresExamType = REQUIRED_EXAM_EVENT_TYPES.has(newSchedule.eventType);
    const availableExamOptions = getExamTypeOptions(newSchedule.eventType, newSchedule.courseId, allCourses);
    if (requiresExamType && !newSchedule.courseId) {
      window.alert('Kies eerst een vak voor dit evenementtype.');
      return;
    }
    if (requiresExamType && availableExamOptions.length === 0) {
      window.alert('Dit vak heeft geen passend examenpunt voor dit evenementtype.');
      return;
    }

    const startDateTime = toParamariboDateTime(newSchedule.date, newSchedule.startTime);
    const endDateTime = toParamariboDateTime(newSchedule.date, newSchedule.endTime);

    const scheduleData = {
      title: newSchedule.title,
      description: newSchedule.description,
      startDateTime,
      endDateTime,
      location: newSchedule.location,
      eventType: newSchedule.eventType,
      examType: newSchedule.examType || undefined,
      studyId: getId(newSchedule.studyId) || undefined,
      studyYearId: getId(newSchedule.studyYearId) || undefined,
      cohortId: getId(newSchedule.cohortId) || undefined,
      mode: newSchedule.mode,
      meetingUrl: newSchedule.mode === 'online' ? newSchedule.meetingUrl : undefined
    };

    try {
      if (newSchedule.eventType === 'college') {
        if (!newSchedule.courseId) return;
        await addSchedule(newSchedule.courseId, scheduleData);
      } else {
        await addCalendarEvent({
          ...scheduleData,
          courseId: getId(newSchedule.courseId) || undefined,
          title: newSchedule.title || EVENT_TYPE_LABELS[newSchedule.eventType] || 'Evenement'
        });
      }

      setNewSchedule({
        eventType: 'college',
        examType: '',
        title: '',
        description: '',
        studyId: '',
        studyYearId: '',
        cohortId: '',
        courseId: '',
        mode: 'onsite',
        meetingUrl: '',
        date: '',
        startTime: '09:00',
        endTime: '17:00',
        location: user?.location || 'Paramaribo'
      });
      setShowAddForm(false);
    } catch (error) {
      window.alert(error.message || 'Opslaan van kalender item mislukt');
    }
  };

  // Get courses filteren op rol/locatie
  const allCourses = (data.courses || []).filter((course) => {
    const courseLocation = (course.location || '').toLowerCase();
    if (user?.role === 'manager' && user?.location) {
      return courseLocation === String(user.location).toLowerCase();
    }
    if (user?.role === 'instructor') {
      return String(course.instructor?.id || course.instructorId || '') === String(user.id || user._id || '');
    }
    if (selectedLocation && selectedLocation !== 'all') {
      return courseLocation === String(selectedLocation).toLowerCase();
    }
    return true;
  });

  const selectedStudyYears = (studies.find((study) => getId(study) === getId(newSchedule.studyId))?.studyYears || [])
    .map((year) => ({ id: getId(year), label: year.name || `Jaar ${year.yearNumber || year.year || ''}` }))
    .filter((year) => year.id);

  const selectedEditStudyYears = (studies.find((study) => getId(study) === getId(editEvent?.studyId))?.studyYears || [])
    .map((year) => ({ id: getId(year), label: year.name || `Jaar ${year.yearNumber || year.year || ''}` }))
    .filter((year) => year.id);

  const availableCohortsForStudy = getId(newSchedule.studyId)
    ? cohorts.filter((cohort) => (cohort.studies || []).some((study) => getId(study) === getId(newSchedule.studyId)))
    : cohorts;

  const availableEditCohortsForStudy = getId(editEvent?.studyId)
    ? cohorts.filter((cohort) => (cohort.studies || []).some((study) => getId(study) === getId(editEvent.studyId)))
    : cohorts;

  const availableCoursesForForm = allCourses.filter((course) => {
    if (newSchedule.studyYearId && String(getId(course.studyYear)) !== String(newSchedule.studyYearId)) {
      return false;
    }
    return true;
  });

  const availableCoursesForEditForm = allCourses.filter((course) => {
    if (editEvent?.studyYearId && String(getId(course.studyYear)) !== String(editEvent.studyYearId)) {
      return false;
    }
    return true;
  });

  return (
    <div className="admin-calendar-page">
      <div className="page-header">
        <h1>{t('admin.calendar')}</h1>
        <button className="btn-primary" onClick={() => setShowAddForm(!showAddForm)}>
          {t('calendar.addEvent')}
        </button>
      </div>

      {/* MODAL: Event Toevoegen */}
      <Modal isOpen={showAddForm} onClose={() => setShowAddForm(false)} title={t('calendar.addEvent')}>
          <form onSubmit={handleAddSchedule} className="schedule-form">
            <div className="schedule-form-group">
              <label>Type</label>
              <select
                value={newSchedule.eventType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  const examOptions = getExamTypeOptions(nextType, newSchedule.courseId, allCourses);
                  const autoTitle = buildAutoTitle(newSchedule.courseId, nextType, allCourses);
                  setNewSchedule((prev) => ({
                    ...prev,
                    eventType: nextType,
                    examType: examOptions.length ? examOptions[0].value : '',
                    title: autoTitle || prev.title
                  }));
                }}
                className="form-select"
              >
                {EVENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* NIEUW: Studie selectie bij toevoegen */}
            <div className="schedule-form-group">
              <label>Studie</label>
              <select
                value={newSchedule.studyId}
                onChange={(e) => setNewSchedule(prev => ({
                  ...prev,
                  studyId: e.target.value,
                  studyYearId: '',
                  cohortId: '',
                  courseId: ''
                }))}
                className="form-select"
              >
                <option value="">-- Selecteer Studie (Optioneel) --</option>
                {studies.map(s => (
                  <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="schedule-form-group">
              <label>Studiejaar</label>
              <select
                value={newSchedule.studyYearId}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, studyYearId: e.target.value, courseId: '' }))}
                className="form-select"
                disabled={!newSchedule.studyId}
              >
                <option value="">-- Selecteer Studiejaar (Optioneel) --</option>
                {selectedStudyYears.map((year) => (
                  <option key={year.id} value={year.id}>{year.label}</option>
                ))}
              </select>
            </div>

            {/* NIEUW: Cohort selectie bij toevoegen */}
            <div className="schedule-form-group">
              <label>Cohort</label>
              <select
                value={newSchedule.cohortId}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, cohortId: e.target.value }))}
                className="form-select"
              >
                <option value="">-- Selecteer Cohort (Optioneel) --</option>
                {availableCohortsForStudy.map(c => (
                  <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
                ))}
              </select>
            </div>

            {REQUIRED_EXAM_EVENT_TYPES.has(newSchedule.eventType) && !newSchedule.courseId && (
              <small className="field-help error-help">Kies eerst een vak om examentypes te laden.</small>
            )}

            {REQUIRED_EXAM_EVENT_TYPES.has(newSchedule.eventType)
              && !!newSchedule.courseId
              && getExamTypeOptions(newSchedule.eventType, newSchedule.courseId, allCourses).length === 0 && (
              <small className="field-help error-help">Dit vak heeft geen passend examenpunt voor dit type.</small>
            )}

            {getExamTypeOptions(newSchedule.eventType, newSchedule.courseId, allCourses).length > 0 && (
              <div className="schedule-form-group">
                <label>Examentype</label>
                <select
                  value={newSchedule.examType || getExamTypeOptions(newSchedule.eventType, newSchedule.courseId, allCourses)[0]?.value || ''}
                  onChange={(e) => setNewSchedule((prev) => ({ ...prev, examType: e.target.value }))}
                  className="form-select"
                >
                  {getExamTypeOptions(newSchedule.eventType, newSchedule.courseId, allCourses).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="schedule-form-group">
              <label>Titel</label>
              <input
                type="text"
                value={newSchedule.title}
                onChange={(e) => setNewSchedule({ ...newSchedule, title: e.target.value })}
                className="form-input"
                placeholder="Bijv. COL101 - Onderwijskunde"
              />
            </div>

            <div className="schedule-form-group">
              <label>Vak koppelen (optioneel)</label>
              <select
                value={newSchedule.courseId}
                onChange={(e) => {
                  const nextCourseId = e.target.value;
                  const examOptions = getExamTypeOptions(newSchedule.eventType, nextCourseId, allCourses);
                  const autoTitle = buildAutoTitle(nextCourseId, newSchedule.eventType, allCourses);
                  
                  // Automatisch proberen de studie/cohort over te nemen van de geselecteerde cursus
                  const selectedCourse = allCourses.find(c => String(c.id || c._id) === String(nextCourseId));
                  
                  setNewSchedule((prev) => ({
                    ...prev,
                    courseId: nextCourseId,
                    examType: examOptions.length ? examOptions[0].value : '',
                    title: autoTitle || prev.title,
                    studyId: getId(selectedCourse?.studyId) || prev.studyId,
                    studyYearId: getId(selectedCourse?.studyYear) || prev.studyYearId,
                    cohortId: getId(selectedCourse?.cohortId) || prev.cohortId
                  }));
                }}
                className="form-select"
              >
                <option value="">-- Geen vak --</option>
                {availableCoursesForForm.map(course => (
                  <option key={course.id || course._id} value={course.id || course._id}>
                    {course.code} - {course.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="schedule-form-group">
              <label>Beschrijving</label>
              <input
                type="text"
                value={newSchedule.description}
                onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
                className="form-input"
                placeholder={t('course.description')}
              />
            </div>

            <div className="schedule-form-group">
              <label>Datum</label>
              <input
                type="date"
                value={newSchedule.date}
                onChange={(e) => setNewSchedule({ ...newSchedule, date: e.target.value })}
                className="form-input"
                required
              />
            </div>

            <div className="schedule-form-group schedule-form-row-2">
              <div>
                <label>Starttijd</label>
                <input
                  type="time"
                  value={newSchedule.startTime}
                  onChange={(e) => setNewSchedule({ ...newSchedule, startTime: e.target.value })}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label>Eindtijd</label>
                <input
                  type="time"
                  value={newSchedule.endTime}
                  onChange={(e) => setNewSchedule({ ...newSchedule, endTime: e.target.value })}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="schedule-form-group">
              <label>Wijze</label>
              <select
                value={newSchedule.mode}
                onChange={(e) => setNewSchedule((prev) => ({ ...prev, mode: e.target.value }))}
                className="form-select"
              >
                <option value="onsite">Fysiek</option>
                <option value="online">Online</option>
              </select>
            </div>

            {newSchedule.mode === 'online' && (
              <div className="schedule-form-group">
                <label>Meeting link</label>
                <input
                  type="url"
                  value={newSchedule.meetingUrl}
                  onChange={(e) => setNewSchedule((prev) => ({ ...prev, meetingUrl: e.target.value }))}
                  className="form-input"
                  placeholder="https://..."
                />
              </div>
            )}

            <div className="schedule-form-group">
              <label>Locatie</label>
              <input
                type="text"
                value={newSchedule.location}
                onChange={(e) => setNewSchedule({ ...newSchedule, location: e.target.value })}
                className="form-input"
                placeholder={t('course.location')}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowAddForm(false)}>{t('common.cancel')}</button>
            </div>
          </form>
      </Modal>

      <Calendar 
        events={getEvents()} 
        onEventClick={handleEventClick}
        editable={true}
      />

      {/* DETAILS VIEW COMPONENT */}
      {selectedEvent && (
        <div className="event-details">
          {(() => {
            const props = selectedEvent.extendedProps?.schedule || selectedEvent.extendedProps || {};
            const eventType = normalizeEventTypeForForm(props.eventType || props.type || 'college', props.examType || '');
            const typeLabel = EVENT_TYPE_LABELS[eventType] || eventType;
            const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: CALENDAR_TIME_ZONE }) : '-';
            const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: CALENDAR_TIME_ZONE }) : '-';
            
            // Namen opzoeken op basis van ID's voor de details tabel
            const currentStudyId = props.studyId || selectedEvent.studyId;
            const currentCohortId = props.cohortId || selectedEvent.cohortId;
            const matchedStudy = studies.find(s => String(s.id || s._id) === String(currentStudyId));
            const matchedCohort = cohorts.find(c => String(c.id || c._id) === String(currentCohortId));

            return (
              <>
                <h3>{selectedEvent.title}</h3>
                <table className="event-details-table">
                  <tbody>
                    <tr><td><strong>Type</strong></td><td><span className={`event-type-badge type-${eventType}`}>{typeLabel}</span></td></tr>
                    <tr><td><strong>Datum</strong></td><td>{fmtDate(selectedEvent.start)}</td></tr>
                    <tr><td><strong>Starttijd</strong></td><td>{fmtTime(selectedEvent.start)}</td></tr>
                    <tr><td><strong>Eindtijd</strong></td><td>{fmtTime(selectedEvent.end)}</td></tr>
                    {props.courseCode && <tr><td><strong>Vakcode</strong></td><td>{props.courseCode}</td></tr>}
                    
                    {/* NIEUW: Zichtbaar gemaakte velden in de admin tabel */}
                    <tr><td><strong>Studie</strong></td><td>{matchedStudy ? matchedStudy.name : '-'}</td></tr>
                    <tr><td><strong>Cohort</strong></td><td>{matchedCohort ? matchedCohort.name : '-'}</td></tr>

                    {props.examType && <tr><td><strong>Examentype</strong></td><td>{props.examType}</td></tr>}
                    <tr><td><strong>Wijze</strong></td><td>{props.mode === 'online' ? 'Online' : 'Fysiek'}</td></tr>
                    {props.location && <tr><td><strong>Locatie</strong></td><td>{props.location}</td></tr>}
                    {props.description && <tr><td><strong>Beschrijving</strong></td><td>{props.description}</td></tr>}
                  </tbody>
                </table>
                {props.mode === 'online' && props.meetingUrl && (
                  <a className="btn-primary" href={props.meetingUrl} target="_blank" rel="noopener noreferrer">Meeting openen</a>
                )}
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <div className="form-actions" style={{ marginTop: '10px' }}>
                    <button className="btn-primary" onClick={openEditEvent}>Bewerken</button>
                    <button className="btn-secondary" onClick={handleDeleteEvent}>Verwijderen</button>
                  </div>
                )}
                <button className="btn-secondary" style={{ marginTop: '8px' }} onClick={() => setSelectedEvent(null)}>Sluiten</button>
              </>
            );
          })()}
        </div>
      )}

      {/* MODAL: Event Bewerken */}
      <Modal isOpen={!!editEvent} onClose={() => setEditEvent(null)} title="Kalender item bewerken">
        {editEvent && (
          <form onSubmit={handleUpdateEvent} className="schedule-form">
            <div className="schedule-form-group">
              <label>Type</label>
              <select
                value={editEvent.eventType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  const options = getExamTypeOptions(nextType, editEvent.courseId, allCourses);
                  const autoTitle = buildAutoTitle(editEvent.courseId, nextType, allCourses);
                  setEditEvent((prev) => ({
                    ...prev,
                    eventType: nextType,
                    examType: options.length ? (options.find((x) => x.value === prev.examType)?.value || options[0].value) : '',
                    title: autoTitle || prev.title
                  }));
                }}
                className="form-select"
              >
                {EVENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* NIEUW: Studie selectie bij bewerken */}
            <div className="schedule-form-group">
              <label>Studie</label>
              <select
                value={editEvent.studyId || ''}
                onChange={(e) => setEditEvent(prev => ({ ...prev, studyId: e.target.value, studyYearId: '', cohortId: '', courseId: '' }))}
                className="form-select"
              >
                <option value="">-- Selecteer Studie (Optioneel) --</option>
                {studies.map(s => (
                  <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="schedule-form-group">
              <label>Studiejaar</label>
              <select
                value={editEvent.studyYearId || ''}
                onChange={(e) => setEditEvent(prev => ({ ...prev, studyYearId: e.target.value, courseId: '' }))}
                className="form-select"
                disabled={!editEvent.studyId}
              >
                <option value="">-- Selecteer Studiejaar (Optioneel) --</option>
                {selectedEditStudyYears.map((year) => (
                  <option key={year.id} value={year.id}>{year.label}</option>
                ))}
              </select>
            </div>

            {/* NIEUW: Cohort selectie bij bewerken */}
            <div className="schedule-form-group">
              <label>Cohort</label>
              <select
                value={editEvent.cohortId || ''}
                onChange={(e) => setEditEvent(prev => ({ ...prev, cohortId: e.target.value }))}
                className="form-select"
              >
                <option value="">-- Selecteer Cohort (Optioneel) --</option>
                {availableEditCohortsForStudy.map(c => (
                  <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="schedule-form-group">
              <label>Vak koppelen (optioneel)</label>
              <select
                value={editEvent.courseId || ''}
                onChange={(e) => {
                  const nextCourseId = e.target.value;
                  const options = getExamTypeOptions(editEvent.eventType, nextCourseId, allCourses);
                  const autoTitle = buildAutoTitle(nextCourseId, editEvent.eventType, allCourses);
                  const selectedCourse = allCourses.find(c => String(c.id || c._id) === String(nextCourseId));

                  setEditEvent((prev) => ({
                    ...prev,
                    courseId: nextCourseId,
                    examType: options.length ? (options.find((x) => x.value === prev.examType)?.value || options[0].value) : '',
                    title: autoTitle || prev.title,
                    studyId: getId(selectedCourse?.studyId) || prev.studyId,
                    studyYearId: getId(selectedCourse?.studyYear) || prev.studyYearId,
                    cohortId: getId(selectedCourse?.cohortId) || prev.cohortId
                  }));
                }}
                className="form-select"
              >
                <option value="">-- Geen vak --</option>
                {availableCoursesForEditForm.map(course => (
                  <option key={course.id || course._id} value={course.id || course._id}>
                    {course.code} - {course.name}
                  </option>
                ))}
              </select>
            </div>

            {REQUIRED_EXAM_EVENT_TYPES.has(editEvent.eventType) && !editEvent.courseId && (
              <small className="field-help error-help">Kies eerst een vak om examentypes te laden.</small>
            )}

            {REQUIRED_EXAM_EVENT_TYPES.has(editEvent.eventType)
              && !!editEvent.courseId
              && getExamTypeOptions(editEvent.eventType, editEvent.courseId, allCourses).length === 0 && (
              <small className="field-help error-help">Dit vak heeft geen passend examenpunt voor dit type.</small>
            )}

            {getExamTypeOptions(editEvent.eventType, editEvent.courseId, allCourses).length > 0 && (
              <div className="schedule-form-group">
                <label>Examentype</label>
                <select
                  value={editEvent.examType || getExamTypeOptions(editEvent.eventType, editEvent.courseId, allCourses)[0]?.value || ''}
                  onChange={(e) => setEditEvent((prev) => ({ ...prev, examType: e.target.value }))}
                  className="form-select"
                >
                  {getExamTypeOptions(editEvent.eventType, editEvent.courseId, allCourses).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="schedule-form-group">
              <label>Titel</label>
              <input
                type="text"
                value={editEvent.title}
                onChange={(e) => setEditEvent((prev) => ({ ...prev, title: e.target.value }))}
                className="form-input"
                placeholder="Titel"
                required
              />
            </div>

            <div className="schedule-form-group">
              <label>Beschrijving</label>
              <input
                type="text"
                value={editEvent.description}
                onChange={(e) => setEditEvent((prev) => ({ ...prev, description: e.target.value }))}
                className="form-input"
                placeholder={t('course.description')}
              />
            </div>

            <div className="schedule-form-group">
              <label>Datum</label>
              <input
                type="date"
                value={editEvent.date}
                onChange={(e) => setEditEvent((prev) => ({ ...prev, date: e.target.value }))}
                className="form-input"
                required
              />
            </div>

            <div className="schedule-form-group schedule-form-row-2">
              <div>
                <label>Starttijd</label>
                <input
                  type="time"
                  value={editEvent.startTime}
                  onChange={(e) => setEditEvent((prev) => ({ ...prev, startTime: e.target.value }))}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label>Eindtijd</label>
                <input
                  type="time"
                  value={editEvent.endTime}
                  onChange={(e) => setEditEvent((prev) => ({ ...prev, endTime: e.target.value }))}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="schedule-form-group">
              <label>Locatie</label>
              <input
                type="text"
                value={editEvent.location}
                onChange={(e) => setEditEvent((prev) => ({ ...prev, location: e.target.value }))}
                className="form-input"
                placeholder={t('course.location')}
              />
            </div>

            <div className="schedule-form-group">
              <label>Wijze</label>
              <select
                value={editEvent.mode || 'onsite'}
                onChange={(e) => setEditEvent((prev) => ({ ...prev, mode: e.target.value }))}
                className="form-select"
              >
                <option value="onsite">Fysiek</option>
                <option value="online">Online</option>
              </select>
            </div>

            {editEvent.mode === 'online' && (
              <div className="schedule-form-group">
                <label>Meeting link</label>
                <input
                  type="url"
                  value={editEvent.meetingUrl || ''}
                  onChange={(e) => setEditEvent((prev) => ({ ...prev, meetingUrl: e.target.value }))}
                  className="form-input"
                  placeholder="https://..."
                />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn-primary">Opslaan</button>
              <button type="button" className="btn-secondary" onClick={() => setEditEvent(null)}>Annuleren</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AdminCalendar;